import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Phone, Calendar, ChevronRight, Bot, PieChart, MessageSquare, Utensils, DollarSign, BarChart3, Check, Headset, X, LogOut, Gamepad2, Zap, Star, Music, Trophy, CircleDot, Heart, Mic as Mic2, Users, Sparkles, CreditCard, ChevronDown, Package, Globe, TrendingUp, Bell, CheckCircle2 } from 'lucide-react';
import { authService, type User } from './lib/auth';
import { dbService } from './lib/database';
import { detectCountry, COUNTRY_CONFIGS, DEFAULT_COUNTRY, type CountryCode } from './lib/pricing';
import { AdminDashboard } from './components/AdminDashboard';
import { Blog } from './components/Blog';
import { BlogPost } from './components/BlogPost';
import { Terms } from './components/Terms';
import { Privacy } from './components/Privacy';
import { About } from './components/About';
import { Header } from './components/Header';
import { SEO } from './components/SEO';

type UserRole = 'user' | 'support' | 'admin';

function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(DEFAULT_COUNTRY);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    smsOptIn: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [authForm, setAuthForm] = useState({ email: '', password: '', fullName: '' });

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setUserRole(currentUser.role);
      } else {
        setUser(null);
        setUserRole(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    detectCountry().then(setSelectedCountry);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/schedule-demo`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
          }),
        }
      );
      if (!res.ok) throw new Error(await res.text());

      setSubmitStatus('success');
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthLoading(true);
    setAuthError(null);
    try {
      const result = authMode === 'signin'
        ? await authService.signIn(authForm.email, authForm.password)
        : await authService.signUp(authForm.email, authForm.password, authForm.fullName);
      setUser(result);
      setUserRole(result.role);
      setIsAuthModalOpen(false);
      setAuthForm({ email: '', password: '', fullName: '' });
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    await authService.signOut();
    setUser(null);
    setUserRole(null);
  };

  const openAuthModal = () => {
    window.location.href = 'https://restroplay.restrocalls.ai/auth';
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  if (user && userRole) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-zinc-900">
        <header className="bg-black/80 backdrop-blur-sm sticky top-0 z-50 border-b border-zinc-800">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-emerald-400 absolute" />
                <Headset className="w-8 h-8 text-emerald-500/70 absolute" />
              </div>
              <span className="font-bold text-xl text-white">RestroCalls.AI</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-zinc-400 hidden sm:inline">{user.email}</span>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-2 text-zinc-300 hover:text-emerald-400 transition"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </nav>
        </header>

        <AdminDashboard userEmail={user.email || ''} userRole={userRole} />
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-b from-black to-zinc-900">
        <Routes>
          <Route path="/blog" element={
            <Blog
              onContactClick={handleContactClick}
              onAuthClick={openAuthModal}
              onDemoClick={() => setIsModalOpen(true)}
            />
          } />
          <Route path="/blog/:slug" element={
            <BlogPost
              onContactClick={handleContactClick}
              onAuthClick={openAuthModal}
              onDemoClick={() => setIsModalOpen(true)}
            />
          } />
          <Route path="/terms" element={
            <Terms
              onContactClick={handleContactClick}
              onAuthClick={openAuthModal}
              onDemoClick={() => setIsModalOpen(true)}
            />
          } />
          <Route path="/privacy" element={
            <Privacy
              onContactClick={handleContactClick}
              onAuthClick={openAuthModal}
              onDemoClick={() => setIsModalOpen(true)}
            />
          } />
          <Route path="/about" element={
            <About
              onContactClick={handleContactClick}
              onAuthClick={openAuthModal}
              onDemoClick={() => setIsModalOpen(true)}
            />
          } />
          <Route path="/" element={
            <>
              <SEO />
              <Header
                onContactClick={handleContactClick}
                onAuthClick={openAuthModal}
                onDemoClick={() => setIsModalOpen(true)}
              />
              {/* Hero Section */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="text-center">
                  <span className="bg-zinc-900 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium inline-block mb-6 border border-zinc-800">
                    Two Powerful Restaurant Solutions
                  </span>
                  <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                    Elevate Your Restaurant with
                    <span className="text-emerald-400"> AI & Entertainment</span>
                  </h1>
                  <p className="text-xl text-zinc-400 mb-10 max-w-3xl mx-auto leading-relaxed">
                    A complete restaurant platform — AI phone handling, guest entertainment, celebration songs, inventory intelligence, and AI marketing, all in one place.
                  </p>

                  <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="bg-emerald-500 text-black px-8 py-3.5 rounded-full hover:bg-emerald-400 transition shadow-lg hover:shadow-emerald-500/20 flex items-center justify-center group">
                      Try Free Demo
                      <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <a href="#pricing" className="border-2 border-emerald-500 text-emerald-400 px-8 py-3.5 rounded-full hover:bg-emerald-500/10 transition">
                      View Pricing
                    </a>
                  </div>
                  <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-xl border border-zinc-800">
                    <iframe
                      width="100%"
                      height="400"
                      src="https://www.youtube.com/embed/_ugm32LhZ60"
                      title="RestroCalls.AI Demo"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full"
                    ></iframe>
                  </div>
                </div>
              </div>

              {/* Services Section */}
              <div id="services" className="bg-zinc-900/50 py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="text-center mb-16">
                    <span className="bg-zinc-900 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium inline-block mb-4 border border-zinc-800">
                      Full Platform
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Five Services, One Platform</h2>
                    <p className="text-zinc-400 max-w-2xl mx-auto">Everything your restaurant needs — answer every call, entertain every guest, celebrate every moment, manage every shelf, and market every opportunity.</p>
                  </div>

                  {/* Service navigator pills */}
                  <div className="flex flex-wrap justify-center gap-2 mb-16">
                    {[
                      { color: "emerald", label: "RestroVoice", icon: <Phone className="w-3.5 h-3.5" /> },
                      { color: "amber",   label: "RestroPlay",  icon: <Gamepad2 className="w-3.5 h-3.5" /> },
                      { color: "rose",    label: "RestroSerenade", icon: <Heart className="w-3.5 h-3.5" /> },
                      { color: "cyan",    label: "RestroStock",    icon: <Package className="w-3.5 h-3.5" /> },
                      { color: "violet",  label: "RestroSocial",   icon: <Globe className="w-3.5 h-3.5" /> },
                    ].map((s) => (
                      <div key={s.label} className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium
                        ${s.color === "emerald" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : ""}
                        ${s.color === "amber"   ? "bg-amber-500/10 border-amber-500/30 text-amber-300" : ""}
                        ${s.color === "rose"    ? "bg-rose-500/10 border-rose-500/30 text-rose-300" : ""}
                        ${s.color === "cyan"    ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-300" : ""}
                        ${s.color === "violet"  ? "bg-violet-500/10 border-violet-500/30 text-violet-300" : ""}
                      `}>
                        {s.icon}
                        <span>{s.label}</span>
                        {s.soon && <span className="text-xs bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-full font-normal">Soon</span>}
                      </div>
                    ))}
                  </div>

                  <div className="space-y-10">

                    {/* ── Service 1: RestroVoice ── */}
                    <div className="rounded-3xl border border-zinc-800 overflow-hidden">
                      <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border-b border-zinc-800 px-8 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                            <Phone className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Service 1 · Subscription</p>
                            <h3 className="text-xl font-bold text-white">RestroVoice — AI Phone Assistant</h3>
                          </div>
                        </div>
                      </div>
                      <div className="p-8 bg-black/20">
                        <p className="text-zinc-400 text-sm mb-7 max-w-3xl">Never miss a call again. RestroVoice handles reservations, menu inquiries, and order taking 24/7 with a natural-sounding AI — fully integrated with your POS.</p>
                        <div className="grid md:grid-cols-3 gap-5">
                          {[
                            { icon: <Phone className="w-6 h-6 text-emerald-400" />, title: "24/7 Call Handling", desc: "Answers every call around the clock — hours, menu, specials — with natural, fluent conversation." },
                            { icon: <Calendar className="w-6 h-6 text-emerald-400" />, title: "Smart Reservations", desc: "Automated table booking that syncs with your existing reservation system in real time." },
                            { icon: <Utensils className="w-6 h-6 text-emerald-400" />, title: "Order Taking", desc: "Accurate phone orders sent directly to your POS — no missed items, no hold music." }
                          ].map((f, i) => (
                            <div key={i} className="bg-zinc-900 p-6 rounded-2xl border border-zinc-800 hover:border-emerald-500/30 transition group">
                              <div className="mb-4 bg-black w-12 h-12 rounded-xl flex items-center justify-center border border-zinc-800 group-hover:scale-110 transition">
                                {f.icon}
                              </div>
                              <h4 className="text-base font-semibold text-white mb-2">{f.title}</h4>
                              <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* ── Service 2: RestroPlay ── */}
                    <div className="rounded-3xl border border-zinc-800 overflow-hidden">
                      <div className="bg-gradient-to-r from-amber-500/10 to-transparent border-b border-zinc-800 px-8 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                            <Gamepad2 className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Service 2 · Subscription</p>
                            <h3 className="text-xl font-bold text-white">RestroPlay — Guest Entertainment Suite</h3>
                          </div>
                        </div>
                        <div className="hidden md:flex gap-2">
                          {[
                            { icon: <Zap className="w-3.5 h-3.5 text-amber-400" />, label: "Multi-round" },
                            { icon: <Bot className="w-3.5 h-3.5 text-amber-400" />, label: "Auto or Host mode" },
                            { icon: <Star className="w-3.5 h-3.5 text-amber-400" />, label: "Custom categories" },
                          ].map((tag, i) => (
                            <div key={i} className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-full">
                              {tag.icon}
                              <span className="text-xs text-amber-300 font-medium">{tag.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="p-8 bg-black/20">
                        <p className="text-zinc-400 text-sm mb-2 max-w-3xl">Four interactive games included in every RestroPlay subscription. Guests join instantly via QR code — no app required.</p>
                        <p className="text-xs text-zinc-600 mb-7">A RestroPlay subscription is also required to access RestroSerenade (Service 3).</p>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                          {[
                            { icon: <Gamepad2 className="w-6 h-6 text-amber-400" />, tag: "Game 1", title: "Trivia Master", desc: "Branded trivia with custom categories. Players join via QR — no app needed.", features: ["Custom question categories", "Live leaderboards", "Multi-round support"] },
                            { icon: <Trophy className="w-6 h-6 text-amber-400" />,   tag: "Game 2", title: "Digital Bingo",   desc: "Classic bingo reimagined — themed nights, happy hour specials, digital cards.", features: ["Themed card designs", "Auto or manual call mode", "Multi-round sessions"] },
                            { icon: <Music className="w-6 h-6 text-amber-400" />,    tag: "Game 3", title: "Singo / Music Bingo", desc: "Turn your playlist into a game. Guests mark off songs as they hear them.", features: ["Music category playlists", "Host-controlled pacing", "Central & custom song lists"] },
                            { icon: <CircleDot className="w-6 h-6 text-amber-400" />, tag: "Game 4", title: "Spin Wheel", desc: "Guests join by phone and spin for prizes, discounts, or offers.", features: ["Join by phone — no app", "Host-triggered spins", "Custom prize & offer slots"], isNew: true },
                          ].map((g, i) => (
                            <div key={i} className={`bg-zinc-900 p-6 rounded-2xl border transition group relative overflow-hidden ${g.isNew ? "border-amber-500/25 hover:border-amber-500/50" : "border-zinc-800 hover:border-amber-500/30"}`}>
                              {g.isNew && <span className="absolute top-3 right-3 text-xs bg-amber-500 text-black px-2 py-0.5 rounded-full font-bold">New</span>}
                              <div className="mb-4 bg-black w-12 h-12 rounded-xl flex items-center justify-center border border-zinc-800 group-hover:scale-110 transition">
                                {g.icon}
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="text-base font-semibold text-white">{g.title}</h4>
                                <span className="text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-medium">{g.tag}</span>
                              </div>
                              <p className="text-zinc-400 text-sm leading-relaxed mb-3">{g.desc}</p>
                              <ul className="space-y-1">
                                {g.features.map((f, j) => (
                                  <li key={j} className="flex items-center gap-2 text-xs text-zinc-400">
                                    <Check className="w-3 h-3 text-amber-400 flex-shrink-0" />{f}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* ── Service 3: RestroSerenade ── */}
                    <div className="rounded-3xl border border-zinc-800 overflow-hidden">
                      <div className="bg-gradient-to-r from-rose-500/10 to-transparent border-b border-zinc-800 px-8 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-rose-400" />
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Service 3 · Pay-as-you-go</p>
                            <h3 className="text-xl font-bold text-white">RestroSerenade — AI Celebration Songs</h3>
                          </div>
                        </div>
                        <span className="hidden md:inline text-xs bg-rose-500/15 border border-rose-500/25 text-rose-300 px-3 py-1.5 rounded-full font-medium">RestroPlay subscription required</span>
                      </div>
                      <div className="p-8 bg-black/20">
                        <p className="text-zinc-400 text-sm mb-7 max-w-3xl">Surprise your guests with a custom AI-generated song crafted just for them. Birthdays, anniversaries, graduations — any milestone worth celebrating becomes an unforgettable moment.</p>
                        <div className="grid md:grid-cols-2 gap-5">
                          <div className="bg-zinc-900 p-6 rounded-2xl border border-rose-500/20 hover:border-rose-500/45 transition group relative overflow-hidden">
                            <span className="absolute top-3 right-3 text-xs bg-rose-500/20 text-rose-400 px-2 py-0.5 rounded-full font-semibold border border-rose-500/30">New</span>
                            <div className="mb-4 bg-black w-12 h-12 rounded-xl flex items-center justify-center border border-zinc-800 group-hover:scale-110 transition">
                              <Heart className="w-6 h-6 text-rose-400" />
                            </div>
                            <h4 className="text-base font-semibold text-white mb-1">RestroSerenade</h4>
                            <p className="text-xs text-rose-400/70 font-medium mb-3">Personalized Celebration Songs</p>
                            <p className="text-zinc-400 text-sm leading-relaxed mb-3">An AI-crafted song personalized with the guest's name and occasion — delivered instantly to the table as an audio keepsake.</p>
                            <ul className="space-y-1">
                              {["Personalized with guest name & occasion", "Birthday, anniversary, graduation & more", "Delivered instantly to the table", "Shareable audio keepsake"].map((f, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                                  <Check className="w-3 h-3 text-rose-400 flex-shrink-0" />{f}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="bg-zinc-900 p-6 rounded-2xl border border-rose-500/20 hover:border-rose-500/45 transition group relative overflow-hidden">
                            <span className="absolute top-3 right-3 text-xs bg-rose-500/20 text-rose-400 px-2 py-0.5 rounded-full font-semibold border border-rose-500/30">New</span>
                            <div className="mb-4 bg-black w-12 h-12 rounded-xl flex items-center justify-center border border-zinc-800 group-hover:scale-110 transition">
                              <Users className="w-6 h-6 text-rose-400" />
                            </div>
                            <h4 className="text-base font-semibold text-white mb-1">Crowd Song</h4>
                            <p className="text-xs text-rose-400/70 font-medium mb-3">Your Restaurant's Anthem</p>
                            <p className="text-zinc-400 text-sm leading-relaxed mb-3">Guests submit words via QR code, the host reviews and curates, then AI weaves it all into one unforgettable restaurant anthem.</p>
                            <ul className="space-y-1">
                              {["Guests submit words via QR code", "Host reviews & curates submissions", "AI generates your restaurant anthem", "Replay at events & share on social"].map((f, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                                  <Check className="w-3 h-3 text-rose-400 flex-shrink-0" />{f}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Service 4: RestroStock ── */}
                    <div className="rounded-3xl border border-zinc-800 overflow-hidden">
                      <div className="bg-gradient-to-r from-cyan-500/10 to-transparent border-b border-zinc-800 px-8 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                            <Package className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Service 4 · Standalone</p>
                            <h3 className="text-xl font-bold text-white">RestroStock — Inventory Intelligence</h3>
                          </div>
                        </div>
                        <span className="hidden md:inline text-xs bg-cyan-500/15 border border-cyan-500/25 text-cyan-300 px-3 py-1.5 rounded-full font-medium">Inventory Management</span>
                      </div>
                      <div className="p-8 bg-black/20">
                        <p className="text-zinc-400 text-sm mb-7 max-w-3xl">Stop running out of your best-selling items mid-service. RestroStock tracks your current inventory in real time and fires low-stock alerts before you hit zero.</p>
                        <div className="grid md:grid-cols-3 gap-5">
                          {[
                            { icon: <Package className="w-6 h-6 text-cyan-400" />, title: "Real-time Inventory", desc: "Track stock levels across all menu items and ingredients from a single dashboard." },
                            { icon: <Bell className="w-6 h-6 text-cyan-400" />, title: "Low Stock Alerts", desc: "Configurable threshold alerts notify you — and your suppliers — before you run out." },
                            { icon: <BarChart3 className="w-6 h-6 text-cyan-400" />, title: "Stock Reports", desc: "Daily and weekly usage reports help you forecast orders and reduce waste." }
                          ].map((f, i) => (
                            <div key={i} className="bg-zinc-900 p-6 rounded-2xl border border-zinc-800 hover:border-cyan-500/30 transition group">
                              <div className="mb-4 bg-black w-12 h-12 rounded-xl flex items-center justify-center border border-zinc-800 group-hover:scale-110 transition">
                                {f.icon}
                              </div>
                              <h4 className="text-base font-semibold text-white mb-2">{f.title}</h4>
                              <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* ── Service 5: RestroSocial ── */}
                    <div className="rounded-3xl border border-zinc-800 overflow-hidden">
                      <div className="bg-gradient-to-r from-violet-500/10 to-transparent border-b border-zinc-800 px-8 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                            <Globe className="w-5 h-5 text-violet-400" />
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Service 5 · Standalone</p>
                            <h3 className="text-xl font-bold text-white">RestroSocial — AI Marketing Intelligence</h3>
                          </div>
                        </div>
                        <span className="hidden md:inline text-xs bg-violet-500/15 border border-violet-500/25 text-violet-300 px-3 py-1.5 rounded-full font-medium">AI Marketing</span>
                      </div>
                      <div className="p-8 bg-black/20">
                        <p className="text-zinc-400 text-sm mb-7 max-w-3xl">Know what's happening in your city before your competitors do. RestroSocial surfaces upcoming major events, local occasions, and community moments — then helps you build timely promotions around each one.</p>
                        <div className="grid md:grid-cols-3 gap-5">
                          {[
                            { icon: <Globe className="w-6 h-6 text-violet-400" />, title: "Event Intelligence", desc: "Automatic feed of upcoming local events, festivals, sports matches, and public holidays near your restaurant." },
                            { icon: <Sparkles className="w-6 h-6 text-violet-400" />, title: "AI Promo Builder", desc: "AI drafts event-specific promotional copy, special menus, and social captions tailored to your brand voice." },
                            { icon: <TrendingUp className="w-6 h-6 text-violet-400" />, title: "Campaign Scheduling", desc: "Schedule posts and offers across social channels in advance — timed to land when your audience is most receptive." }
                          ].map((f, i) => (
                            <div key={i} className="bg-zinc-900 p-6 rounded-2xl border border-zinc-800 hover:border-violet-500/30 transition group">
                              <div className="mb-4 bg-black w-12 h-12 rounded-xl flex items-center justify-center border border-zinc-800 group-hover:scale-110 transition">
                                {f.icon}
                              </div>
                              <h4 className="text-base font-semibold text-white mb-2">{f.title}</h4>
                              <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div id="pricing" className="py-24 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="text-center mb-6">
                    <span className="bg-zinc-900 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium inline-block mb-4 border border-zinc-800">
                      Flexible Plans
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Simple, Transparent Pricing</h2>
                    <p className="text-zinc-400 max-w-2xl mx-auto">Choose the plan that fits your restaurant. Subscribe to one service or bundle both for maximum savings.</p>
                  </div>

                  {/* Country selector */}
                  <div className="flex justify-center mb-10">
                    <div className="relative">
                      <button
                        onClick={() => setCountryDropdownOpen(v => !v)}
                        className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 hover:border-zinc-500 px-4 py-2 rounded-full text-sm text-zinc-300 transition"
                      >
                        <span>{COUNTRY_CONFIGS[selectedCountry].flag}</span>
                        <span>{COUNTRY_CONFIGS[selectedCountry].name}</span>
                        <span className="text-xs text-zinc-500 ml-1">{COUNTRY_CONFIGS[selectedCountry].currency}</span>
                        <ChevronDown className="w-3.5 h-3.5 text-zinc-500 ml-1" />
                      </button>
                      {countryDropdownOpen && (
                        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden shadow-xl z-20 w-52">
                          {(Object.keys(COUNTRY_CONFIGS) as CountryCode[]).map(code => (
                            <button
                              key={code}
                              onClick={() => { setSelectedCountry(code); setCountryDropdownOpen(false); }}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-zinc-800 transition text-left ${selectedCountry === code ? 'text-white' : 'text-zinc-400'}`}
                            >
                              <span>{COUNTRY_CONFIGS[code].flag}</span>
                              <span>{COUNTRY_CONFIGS[code].name}</span>
                              <span className="ml-auto text-xs text-zinc-600">{COUNTRY_CONFIGS[code].currency}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Service labels */}
                  <div className="flex flex-wrap justify-center gap-3 mb-12">
                    <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-4 py-2 rounded-full">
                      <Phone className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-zinc-300 font-medium">RestroCalls AI</span>
                    </div>
                    <div className="flex items-center gap-2 bg-zinc-900 border border-amber-500/30 px-4 py-2 rounded-full">
                      <Gamepad2 className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-sm text-amber-300 font-medium">RestroPlay</span>
                      <span className="text-xs text-zinc-500 font-normal">Games + Serenade & Crowd Song (quota + PAYG extras)</span>
                    </div>
                  </div>

                  {(() => {
                    const config = COUNTRY_CONFIGS[selectedCountry];
                    const { pricing } = config;
                    return (
                      <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">

                        {/* RestroCalls Only */}
                        <div className="bg-zinc-900 rounded-3xl border border-zinc-800 flex flex-col overflow-hidden">
                          <div className="p-8 flex flex-col flex-1">
                            <div className="flex items-center gap-3 mb-5">
                              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                                <Phone className="w-5 h-5 text-emerald-400" />
                              </div>
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Individual</p>
                                <h3 className="text-lg font-bold text-white">RestroCalls AI</h3>
                              </div>
                            </div>
                            <div className="flex items-end gap-1 mb-2">
                              <span className="text-4xl font-bold text-white">{pricing.restrocalls.display}</span>
                              <span className="text-zinc-400 mb-1">/mo</span>
                            </div>
                            <p className="text-sm text-zinc-500 mb-6">No contract &bull; Cancel anytime</p>
                            <div className="space-y-3 mb-8 flex-1">
                              {[
                                "Unlimited AI Phone Calls",
                                "24/7 Call Availability",
                                "Multi-language Support",
                                "Real-time POS Integration",
                                "Instant Call Analytics",
                                "Custom Voice Configuration",
                                "Smart Reservation Management",
                                "Order Taking Automation"
                              ].map((feature, i) => (
                                <div key={i} className="flex items-center gap-2.5">
                                  <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-emerald-400" />
                                  </div>
                                  <span className="text-sm text-zinc-300">{feature}</span>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() => setIsModalOpen(true)}
                              className="w-full py-3 rounded-xl border border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 transition font-medium text-sm">
                              Start Free Trial
                            </button>
                            <p className="text-xs text-center text-zinc-600 mt-3">14-day free trial included</p>
                          </div>
                        </div>

                        {/* Bundle - Most Popular */}
                        <div className="bg-zinc-900 rounded-3xl border border-emerald-500/40 flex flex-col overflow-hidden relative shadow-xl shadow-emerald-500/5">
                          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-8 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4 text-black fill-black" />
                              <span className="text-black text-sm font-bold">Most Popular</span>
                            </div>
                            <span className="bg-black/20 text-black text-xs font-bold px-2 py-0.5 rounded-full">{pricing.bundle.savingsBadge}</span>
                          </div>
                          <div className="p-8 flex flex-col flex-1">
                            <div className="flex items-center gap-3 mb-5">
                              <div className="flex -space-x-2">
                                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
                                  <Phone className="w-4 h-4 text-emerald-400" />
                                </div>
                                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                                  <Gamepad2 className="w-4 h-4 text-amber-400" />
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Bundle</p>
                                <h3 className="text-lg font-bold text-white">Complete Package</h3>
                              </div>
                            </div>
                            <div className="flex items-end gap-2 mb-1">
                              <span className="text-4xl font-bold text-white">{pricing.bundle.display}</span>
                              <span className="text-zinc-400 mb-1">/mo</span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm text-zinc-600 line-through">{pricing.bundle.originalDisplay}</span>
                              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-medium">{pricing.bundle.savings}</span>
                            </div>
                            <p className="text-sm text-zinc-500 mb-6">All services, one bill &bull; Cancel anytime</p>
                            <div className="space-y-3 mb-6 flex-1">
                              <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Everything in RestroCalls +</p>
                              {[
                                "RestroPlay — all 4 games included",
                                "Trivia Master (multi-round)",
                                "Digital Bingo (multi-round)",
                                "Singo / Music Bingo (multi-round)",
                                "Spin Wheel — guests join by phone",
                                "Manual Host or Auto Mode",
                                "Central & custom categories",
                                "Custom prize & offer slots",
                                "Priority Support & Account Manager"
                              ].map((feature, i) => (
                                <div key={i} className="flex items-center gap-2.5">
                                  <div className="w-5 h-5 rounded-full bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-teal-400" />
                                  </div>
                                  <span className="text-sm text-zinc-300">{feature}</span>
                                </div>
                              ))}
                              <div className="pt-2 pb-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="flex-1 h-px bg-zinc-800" />
                                  <span className="text-xs text-zinc-600 font-medium">pay-as-you-go</span>
                                  <div className="flex-1 h-px bg-zinc-800" />
                                </div>
                              </div>
                              {[
                                "RestroSerenade — pay-as-you-go",
                                "Crowd Song — pay-as-you-go"
                              ].map((feature, i) => (
                                <div key={i} className="flex items-center gap-2.5">
                                  <div className="w-5 h-5 rounded-full bg-rose-500/15 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-rose-400" />
                                  </div>
                                  <span className="text-sm text-zinc-300">{feature}</span>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() => setIsModalOpen(true)}
                              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-black hover:from-emerald-400 hover:to-teal-400 transition font-semibold text-sm shadow-lg shadow-emerald-500/20">
                              Get Started — Best Value
                            </button>
                            <p className="text-xs text-center text-zinc-600 mt-3">14-day free trial included</p>
                          </div>
                        </div>

                        {/* RestroPlay */}
                        <div className="bg-zinc-900 rounded-3xl border border-zinc-800 flex flex-col overflow-hidden">
                          <div className="p-8 flex flex-col flex-1">
                            <div className="flex items-center gap-3 mb-5">
                              <div className="flex -space-x-2">
                                <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                                  <Gamepad2 className="w-3.5 h-3.5 text-amber-400" />
                                </div>
                                <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                                  <Trophy className="w-3.5 h-3.5 text-amber-400" />
                                </div>
                                <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                                  <Music className="w-3.5 h-3.5 text-amber-400" />
                                </div>
                                <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                                  <CircleDot className="w-3.5 h-3.5 text-amber-400" />
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Individual</p>
                                <h3 className="text-lg font-bold text-white">RestroPlay</h3>
                              </div>
                            </div>
                            <div className="flex items-end gap-1 mb-1">
                              <span className="text-4xl font-bold text-white">{pricing.restroplay.display}</span>
                              <span className="text-zinc-400 mb-1">/mo</span>
                            </div>
                            <p className="text-xs text-amber-400/80 font-medium mb-1">RestroPlay — Trivia · Bingo · Singo · Spin Wheel</p>
                            <p className="text-sm text-zinc-500 mb-6">No contract &bull; Cancel anytime</p>
                            <div className="space-y-3 mb-8 flex-1">
                              <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">All 4 Games +</p>
                              {[
                                "Multi-round capabilities",
                                "Manual Host or Auto Mode",
                                "Central categories library",
                                "Host & restaurant-specific categories",
                                "Spin Wheel — guests join by phone",
                                "Custom prize & offer slots",
                                "Live Leaderboards",
                                "Game Analytics Dashboard"
                              ].map((feature, i) => (
                                <div key={i} className="flex items-center gap-2.5">
                                  <div className="w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-amber-400" />
                                  </div>
                                  <span className="text-sm text-zinc-300">{feature}</span>
                                </div>
                              ))}
                              <div className="pt-2 pb-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="flex-1 h-px bg-zinc-800" />
                                  <span className="text-xs text-zinc-600 font-medium">pay-as-you-go</span>
                                  <div className="flex-1 h-px bg-zinc-800" />
                                </div>
                              </div>
                              {[
                                "RestroSerenade — pay-as-you-go",
                                "Crowd Song — pay-as-you-go"
                              ].map((feature, i) => (
                                <div key={i} className="flex items-center gap-2.5">
                                  <div className="w-5 h-5 rounded-full bg-rose-500/15 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-rose-400" />
                                  </div>
                                  <span className="text-sm text-zinc-300">{feature}</span>
                                </div>
                              ))}
                            </div>
                            <a
                              href="https://restroplay.restrocalls.ai/auth"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full py-3 rounded-xl border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 transition font-medium text-sm flex items-center justify-center">
                              Start Free Trial
                            </a>
                            <p className="text-xs text-center text-zinc-600 mt-3">14-day free trial included</p>
                          </div>
                        </div>

                      </div>
                    );
                  })()}

                  {/* Bottom trust strip */}
                  <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-zinc-500">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-emerald-500" />
                      <span>Enable or disable anytime</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>No long-term contract</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>No credit card for trial</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>Cancel with 1-click</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Section */}
              <div id="features" className="py-24 bg-zinc-900/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <h2 className="text-3xl font-bold text-center mb-16 text-white">Advanced Features</h2>
                  <div className="grid md:grid-cols-4 gap-8">
                    {[
                      {
                        icon: <DollarSign className="w-6 h-6 text-emerald-400" />,
                        title: "POS Integration",
                        description: "Seamless connection with your existing systems"
                      },
                      {
                        icon: <BarChart3 className="w-6 h-6 text-emerald-400" />,
                        title: "Call Analytics",
                        description: "Detailed insights and reporting"
                      },
                      {
                        icon: <MessageSquare className="w-6 h-6 text-emerald-400" />,
                        title: "Multi-language",
                        description: "Support in multiple languages"
                      },
                      {
                        icon: <PieChart className="w-6 h-6 text-emerald-400" />,
                        title: "Order Accuracy",
                        description: "99.9% order accuracy rate"
                      }
                    ].map((feature, index) => (
                      <div key={index} className="text-center group">
                        <div className="bg-black w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition border border-zinc-800">
                          {feature.icon}
                        </div>
                        <h3 className="font-semibold mb-2 text-white">{feature.title}</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed">{feature.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-black py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                  <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Restaurant's Phone Service?</h2>
                  <p className="text-zinc-400 mb-10 max-w-2xl mx-auto">
                    Join hundreds of restaurants that have improved their customer service and increased revenue with RestroCalls.AI
                  </p>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-emerald-500 text-black px-8 py-3.5 rounded-full hover:bg-emerald-400 transition shadow-lg hover:shadow-emerald-500/20">
                    Start Free Trial
                  </button>
                </div>
              </div>

              {/* Footer */}
              <footer className="bg-black text-zinc-400 py-16 border-t border-zinc-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="grid md:grid-cols-4 gap-12">
                    <div>
                      <div className="flex items-center space-x-2 mb-6">
                        <div className="relative w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center">
                          <Bot className="w-4 h-4 text-emerald-400 absolute" />
                          <Headset className="w-6 h-6 text-emerald-500/70 absolute" />
                        </div>
                        <span className="font-bold text-white">RestroCalls.AI</span>
                      </div>
                      <p className="text-sm leading-relaxed">
                        Revolutionizing restaurant phone service with intelligent AI solutions.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-4">Solutions</h4>
                      <ul className="space-y-3 text-sm">
                        <li className="hover:text-emerald-400 transition cursor-pointer">Call Handling</li>
                        <li className="hover:text-emerald-400 transition cursor-pointer">Reservation Management</li>
                        <li className="hover:text-emerald-400 transition cursor-pointer">Order Processing</li>
                        <li className="hover:text-emerald-400 transition cursor-pointer">POS Integration</li>
                        <li className="hover:text-amber-400 transition cursor-pointer">RestroPlay</li>
                        <li className="hover:text-rose-400 transition cursor-pointer">RestroSerenade & Crowd Song</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-4">Company</h4>
                      <ul className="space-y-3 text-sm">
                        <li><Link to="/about" className="hover:text-emerald-400 transition cursor-pointer">About Us</Link></li>
                        <li className="hover:text-emerald-400 transition cursor-pointer">Case Studies</li>
                        <li><Link to="/blog" className="hover:text-emerald-400 transition cursor-pointer">Blog</Link></li>
                        <li><Link to="/terms" className="hover:text-emerald-400 transition cursor-pointer">Terms</Link></li>
                        <li><Link to="/privacy" className="hover:text-emerald-400 transition cursor-pointer">Privacy</Link></li>
                        <li>
                          <button
                            onClick={handleContactClick}
                            className="hover:text-emerald-400 transition cursor-pointer"
                          >
                            Contact
                          </button>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-4">Contact</h4>
                      <ul className="space-y-3 text-sm">
                        <li>support@restrocalls.ai</li>
                        <li>
                          <a href="tel:5123529511" className="hover:text-emerald-400 transition">(512) 352-9511</a>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="border-t border-zinc-800 mt-16 pt-8 text-sm text-center">
                    &copy; 2025 RestroCalls.AI. All rights reserved.
                  </div>
                </div>
              </footer>
            </>
          } />
        </Routes>

        {/* Contact Form Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-zinc-900 rounded-2xl w-full max-w-md p-6 relative border border-zinc-800">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSubmitStatus('idle');
                  setFormData({ name: '', email: '', phone: '', message: '', smsOptIn: false });
                }}
                className="absolute right-4 top-4 text-zinc-400 hover:text-white transition">
                <X className="w-5 h-5" />
              </button>

              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-9 h-9 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
                  <p className="text-zinc-400 leading-relaxed mb-2">
                    Thanks for reaching out to us. We've received your message and
                  </p>
                  <p className="text-zinc-400 leading-relaxed mb-8">
                    our team will get in touch with you shortly.
                  </p>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setSubmitStatus('idle');
                      setFormData({ name: '', email: '', phone: '', message: '', smsOptIn: false });
                    }}
                    className="w-full py-3 rounded-lg font-medium bg-emerald-500 text-black hover:bg-emerald-400 transition">
                    Close
                  </button>
                </div>
              ) : (
                <>
              <h3 className="text-2xl font-bold text-white mb-6">Contact Us</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-zinc-300 mb-1">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-black border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-zinc-300 mb-1">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-black border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-zinc-300 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-black border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition"
                    placeholder="Your phone number"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-zinc-300 mb-1">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 bg-black border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition resize-none"
                    placeholder="Tell us about your restaurant..."
                  />
                </div>

                <div className="flex items-start space-x-3 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                  <input
                    type="checkbox"
                    id="smsOptIn"
                    name="smsOptIn"
                    checked={formData.smsOptIn}
                    onChange={handleInputChange}
                    className="mt-1 w-4 h-4 text-emerald-500 bg-zinc-800 border-zinc-600 rounded focus:ring-emerald-500 focus:ring-2"
                  />
                  <label htmlFor="smsOptIn" className="text-sm text-zinc-300 leading-relaxed">
                    I agree to receive SMS marketing messages and notifications from RestroCalls.ai. By opting in, I agree to the{' '}
                    <Link
                      to="/terms"
                      className="text-emerald-400 hover:text-emerald-300 underline transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms and Conditions
                    </Link>
                    {' '}and{' '}
                    <Link
                      to="/privacy"
                      className="text-emerald-400 hover:text-emerald-300 underline transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 rounded-lg font-medium transition ${
                    submitStatus === 'success'
                      ? 'bg-green-500 text-white'
                      : submitStatus === 'error'
                      ? 'bg-red-500 text-white'
                      : 'bg-emerald-500 text-black hover:bg-emerald-400'
                  } ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Sending...' :
                   submitStatus === 'error' ? 'Error Sending — Try Again' :
                   'Send Message'}
                </button>
              </form>
                </>
              )}
            </div>
          </div>
        )}
        {/* Auth Modal */}
        {isAuthModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-zinc-900 rounded-2xl w-full max-w-sm p-6 relative border border-zinc-800">
              <button
                onClick={() => setIsAuthModalOpen(false)}
                className="absolute right-4 top-4 text-zinc-400 hover:text-white transition">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 mb-6">
                <div className="relative w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4 text-emerald-400 absolute" />
                  <Headset className="w-6 h-6 text-emerald-500/70 absolute" />
                </div>
                <span className="font-bold text-white">RestroCalls.AI</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-1">
                {authMode === 'signin' ? 'Sign In' : 'Create Account'}
              </h3>
              <p className="text-sm text-zinc-500 mb-6">
                {authMode === 'signin'
                  ? 'Welcome back. Sign in to your dashboard.'
                  : 'Set up your RestroCalls.AI account.'}
              </p>
              <form onSubmit={handleAuthSubmit} className="space-y-4">
                {authMode === 'signup' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">Full Name</label>
                    <input
                      type="text"
                      required
                      value={authForm.fullName}
                      onChange={e => setAuthForm(p => ({ ...p, fullName: e.target.value }))}
                      className="w-full px-4 py-2 bg-black border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition"
                      placeholder="Your name"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={authForm.email}
                    onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-4 py-2 bg-black border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition"
                    placeholder="you@restaurant.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Password</label>
                  <input
                    type="password"
                    required
                    value={authForm.password}
                    onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))}
                    className="w-full px-4 py-2 bg-black border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition"
                    placeholder="••••••••"
                  />
                </div>
                {authError && (
                  <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">{authError}</p>
                )}
                <button
                  type="submit"
                  disabled={isAuthLoading}
                  className="w-full py-2.5 rounded-lg font-medium bg-emerald-500 text-black hover:bg-emerald-400 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isAuthLoading
                    ? (authMode === 'signin' ? 'Signing in...' : 'Creating account...')
                    : (authMode === 'signin' ? 'Sign In' : 'Create Account')}
                </button>
              </form>
              <p className="text-sm text-center text-zinc-500 mt-4">
                {authMode === 'signin' ? "Don't have an account?" : 'Already have an account?'}{' '}
                <button
                  onClick={() => { setAuthMode(authMode === 'signin' ? 'signup' : 'signin'); setAuthError(null); }}
                  className="text-emerald-400 hover:text-emerald-300 transition font-medium"
                >
                  {authMode === 'signin' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </Router>
  );
}

export default App;
